import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Loader2, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  { code: 'IN', name: 'India', flag: '🇮🇳', dial: '+91', idLabel: 'PAN', idPlaceholder: 'ABCDE1234F', idPattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/ },
  { code: 'US', name: 'United States', flag: '🇺🇸', dial: '+1', idLabel: 'SSN (last 4)', idPlaceholder: '1234', idPattern: /^\d{4}$/ },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dial: '+44', idLabel: 'NI Number', idPlaceholder: 'QQ123456C', idPattern: /^[A-Z]{2}\d{6}[A-Z]$/ },
  { code: 'AE', name: 'UAE', flag: '🇦🇪', dial: '+971', idLabel: 'Emirates ID', idPlaceholder: '784-XXXX-XXXXXXX-X', idPattern: /^784/ },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dial: '+1', idLabel: 'SIN (last 4)', idPlaceholder: '1234', idPattern: /^\d{4}$/ },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', dial: '+61', idLabel: 'TFN (last 4)', idPlaceholder: '1234', idPattern: /^\d{4}$/ },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', dial: '+65', idLabel: 'NRIC', idPlaceholder: 'S1234567D', idPattern: /^[STFGM]\d{7}[A-Z]$/ },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', dial: '+49', idLabel: 'Tax ID', idPlaceholder: '12345678901', idPattern: /^\d{11}$/ },
  { code: 'FR', name: 'France', flag: '🇫🇷', dial: '+33', idLabel: 'NIR (last 5)', idPlaceholder: '12345', idPattern: /^\d{5}$/ },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', dial: '+81', idLabel: 'My Number (last 4)', idPlaceholder: '1234', idPattern: /^\d{4}$/ },
  { code: 'OTHER', name: 'Other', flag: '🌍', dial: '+', idLabel: 'National ID', idPlaceholder: 'Your national ID number', idPattern: /.+/ },
];

const disciplineOptions = [
  { value: 'fine_arts', label: 'Fine Arts (Painting, Sculpture, etc.)' },
  { value: 'performing_arts', label: 'Performing Arts (Dance, Theatre, Music)' },
  { value: 'film_production', label: 'Film & Production' },
  { value: 'writing', label: 'Writing & Literature' },
  { value: 'design', label: 'Design (Graphic, Fashion, Industrial)' },
  { value: 'photography', label: 'Photography & Visual Media' },
  { value: 'digital_arts', label: 'Digital Arts & New Media' },
  { value: 'music_production', label: 'Music Production & Composition' },
  { value: 'crafts', label: 'Traditional Crafts & Artisanship' },
  { value: 'other', label: 'Other' },
];

const CountrySelector = ({ value, onChange, label }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="input-ainaa w-full h-12 px-4 border-[#E8E4DE]" data-testid="waitlist-country-select">
        <SelectValue placeholder="Select country" />
      </SelectTrigger>
      <SelectContent className="bg-[#EDECEA] border-[#D8D3CB] z-[200] max-h-[300px]">
        {COUNTRIES.map(c => (
          <SelectItem key={c.code} value={c.code} className="text-[#4A4640] font-medium focus:bg-[#F0EDE8] focus:text-[#0F1B2D]">
            <span className="mr-2">{c.flag}</span> {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const IdConfirmDialog = ({ idValue, idLabel, onConfirm, onCancel }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="absolute inset-0 z-[210] flex items-center justify-center bg-[#0F1B2D]/60 backdrop-blur-sm p-4">
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
      className="bg-[#EDECEA] p-8 border-2 border-[#0F1B2D] max-w-sm w-full">
      <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#0F1B2D] mb-3">Confirm {idLabel}</h4>
      <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-4">
        Please verify the {idLabel} below is correct. Once submitted, this {idLabel} will be permanently linked to your AINAA profile and cannot be modified.
      </p>
      <div className="bg-[#EDECEA] border-2 border-[#0F1B2D] p-5 mb-6 text-center">
        <p className="text-[9px] uppercase tracking-[0.25em] text-[#9B9589] font-bold mb-2">Your {idLabel}</p>
        <p className="text-3xl font-bold text-[#0F1B2D] tracking-[0.35em]"
          style={{ fontFamily: "'Courier New', Courier, monospace" }} data-testid="id-confirm-value">{idValue}</p>
      </div>
      <div className="flex gap-3">
        <button onClick={onConfirm} className="flex-1 btn-ainaa-primary py-3 text-xs font-semibold uppercase tracking-wider" data-testid="id-confirm-submit">Confirm & Submit</button>
        <button onClick={onCancel} className="flex-1 btn-ainaa-secondary py-3 text-xs font-semibold uppercase tracking-wider" data-testid="id-confirm-cancel">Go Back</button>
      </div>
    </motion.div>
  </motion.div>
);

const WaitlistModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', profession: '', improvement_area: '',
    pan: '', experience_years: '', country: 'IN', imdb_link: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [showIdConfirm, setShowIdConfirm] = useState(false);
  const [copied, setCopied] = useState(false);

  const selectedCountry = COUNTRIES.find(c => c.code === formData.country) || COUNTRIES[0];

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.profession || !formData.pan || !formData.phone) {
      toast.error('Please complete all required fields');
      return false;
    }
    if (!selectedCountry.idPattern.test(formData.pan.toUpperCase())) {
      toast.error(`Please enter a valid ${selectedCountry.idLabel}`);
      return false;
    }
    if (formData.imdb_link && !/^https?:\/\/(www\.)?imdb\.com\//.test(formData.imdb_link)) {
      toast.error('Please enter a valid IMDB URL');
      return false;
    }
    return true;
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowIdConfirm(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowIdConfirm(false);
    setIsLoading(true);
    try {
      const phoneWithCode = formData.phone.startsWith('+') ? formData.phone : `${selectedCountry.dial} ${formData.phone}`;
      const response = await axios.post(`${API}/waitlist`, {
        name: formData.name, email: formData.email,
        phone: phoneWithCode, profession: formData.profession,
        improvement_area: formData.improvement_area || null,
        pan: formData.pan.toUpperCase(),
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
        country: formData.country,
        imdb_link: formData.imdb_link || null,
      });
      if (response.data.success) {
        setPassword(response.data.password || response.data.application_id || '');
        setIsSuccess(true);
      } else {
        toast.info(response.data.message);
        if (response.data.application_id) { setPassword(response.data.password || response.data.application_id); setIsSuccess(true); }
      }
    } catch { toast.error('Application could not be submitted. Please try again.'); }
    finally { setIsLoading(false); }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({ name: '', email: '', phone: '', profession: '', improvement_area: '', pan: '', experience_years: '', country: 'IN', imdb_link: '' });
      setIsSuccess(false); setPassword(''); setShowIdConfirm(false); setCopied(false);
      onClose();
    }
  };

  const set = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  const copyPassword = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Password copied');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="waitlist-modal">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-[#0F1B2D]/40 backdrop-blur-sm" onClick={handleClose} />
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="bg-[#EDECEA] p-8 md:p-10 shadow-2xl border-2 border-[#0F1B2D]">
              <button onClick={handleClose} className="absolute top-4 right-4 p-2 text-[#9B9589] hover:text-[#0F1B2D] transition-colors"
                disabled={isLoading} data-testid="waitlist-close-btn"><X size={20} /></button>

              {isSuccess ? (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-[#C4A35A] mx-auto mb-4" />
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0F1B2D] mb-2">You're on the Waitlist</h3>
                  {password && (
                    <div className="my-4 inline-block border-2 border-[#0F1B2D] px-5 py-3 bg-white">
                      <p className="text-[9px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-1">Your Password</p>
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-lg font-bold text-[#0F1B2D]" data-testid="password-display">{password}</p>
                        <button onClick={copyPassword} className="text-[#9B9589] hover:text-[#0F1B2D]" data-testid="copy-password-btn">
                          {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-[#4A4640] font-medium text-sm mt-3">Save your password securely. You'll use your email and this password to log in and track your application.</p>
                  <button onClick={handleClose} className="btn-ainaa-secondary px-6 py-2.5 text-xs font-semibold uppercase tracking-wider mt-6" data-testid="waitlist-done-btn">Done</button>
                </motion.div>
              ) : (
                <>
                  <AnimatePresence>
                    {showIdConfirm && <IdConfirmDialog idValue={formData.pan.toUpperCase()} idLabel={selectedCountry.idLabel} onConfirm={handleConfirmedSubmit} onCancel={() => setShowIdConfirm(false)} />}
                  </AnimatePresence>

                  <div className="text-center mb-8">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#C4A35A] font-bold mb-3">Join the Waitlist</p>
                    <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-semibold text-[#0F1B2D] mb-3">Request Membership</h3>
                    <p className="text-[#4A4640] font-medium text-sm">Membership requires verification and recommendation from a minimum of 3 existing platform members.</p>
                  </div>

                  <form onSubmit={handleSubmitClick} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">Full Name *</label>
                        <Input type="text" value={formData.name} onChange={(e) => set('name', e.target.value)}
                          placeholder="As on official docs" className="input-ainaa w-full h-12 px-4"
                          disabled={isLoading} data-testid="waitlist-name-input" />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">Email Address *</label>
                        <Input type="email" value={formData.email} onChange={(e) => set('email', e.target.value)}
                          placeholder="your@email.com" className="input-ainaa w-full h-12 px-4"
                          disabled={isLoading} data-testid="waitlist-email-input" />
                      </div>
                    </div>

                    <CountrySelector value={formData.country} onChange={(code) => { set('country', code); set('pan', ''); }} label="Present Country *" />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">Phone Number *</label>
                        <div className="flex gap-2">
                          <div className="input-ainaa h-12 px-3 flex items-center gap-1 text-sm flex-shrink-0 bg-[#F8F7F5]">
                            <span className="text-base">{selectedCountry.flag}</span>
                            <span className="text-[#0F1B2D] font-medium text-xs">{selectedCountry.dial}</span>
                          </div>
                          <Input type="tel" value={formData.phone} onChange={(e) => set('phone', e.target.value)}
                            placeholder="98765 43210" className="input-ainaa w-full h-12 px-4"
                            disabled={isLoading} data-testid="waitlist-phone-input" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">{selectedCountry.idLabel} *</label>
                        <Input type="text" value={formData.pan} onChange={(e) => set('pan', e.target.value.toUpperCase())}
                          placeholder={selectedCountry.idPlaceholder} className="input-ainaa w-full h-12 px-4 uppercase"
                          disabled={isLoading} data-testid="waitlist-pan-input" />
                        <p className="text-[10px] text-[#9B9589] font-medium mt-1">Confirmed before submission</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">Artistic Discipline *</label>
                        <Input type="text" value={formData.profession} onChange={(e) => set('profession', e.target.value)}
                          placeholder="e.g., Filmmaker, Musician" className="input-ainaa w-full h-12 px-4"
                          disabled={isLoading} data-testid="waitlist-profession-input" />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">Primary Field</label>
                        <Select value={formData.improvement_area} onValueChange={(v) => set('improvement_area', v)} disabled={isLoading}>
                          <SelectTrigger className="input-ainaa w-full h-12 px-4 border-[#E8E4DE]" data-testid="waitlist-improvement-select">
                            <SelectValue placeholder="Select field" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#EDECEA] border-[#D8D3CB] z-[200]">
                            {disciplineOptions.map(o => (
                              <SelectItem key={o.value} value={o.value} className="text-[#4A4640] font-medium focus:bg-[#F0EDE8] focus:text-[#0F1B2D]">{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">Experience (Years)</label>
                        <Input type="number" min="0" max="80" value={formData.experience_years}
                          onChange={(e) => set('experience_years', e.target.value)}
                          placeholder="Years" className="input-ainaa w-full h-12 px-4"
                          disabled={isLoading} data-testid="waitlist-experience-input" />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">IMDB Profile</label>
                        <Input type="url" value={formData.imdb_link} onChange={(e) => set('imdb_link', e.target.value)}
                          placeholder="imdb.com/..." className="input-ainaa w-full h-12 px-4"
                          disabled={isLoading} data-testid="waitlist-imdb-input" />
                      </div>
                    </div>

                    <button type="submit" disabled={isLoading}
                      className="w-full btn-ainaa-primary py-4 text-sm font-semibold flex items-center justify-center gap-2 mt-6 uppercase tracking-wider"
                      data-testid="waitlist-submit-btn">
                      {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Submitting...</> : 'Join the Waitlist'}
                    </button>
                  </form>

                  <p className="text-center text-xs font-medium text-[#9B9589] mt-6">
                    By applying, you agree to the Association's{' '}
                    <a href="/privacy" className="text-[#4A4640] hover:text-[#0F1B2D] transition-colors underline">Privacy Policy</a>{' '}and{' '}
                    <a href="/terms" className="text-[#4A4640] hover:text-[#0F1B2D] transition-colors underline">Terms</a>
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WaitlistModal;
